import { Badge, Flex, Text } from '@mantine/core';
import {
	DocumentWorkflowInstanceSignatureStatus,
	SubjectAction,
} from '@dealstack/shared/lib/generated';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date';
import {
	useTranslationMipCreate,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DocumentStatusDot } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/components/DocumentStatusDot';
import { REQUESTED_STATUS } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/components/utils';
import {
	BadgeColumn,
	CardBody,
	CardContainer,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/FormsCategoryList/styled';
import {
	FormReviewCardData,
	isAwaitingRequiredSignature,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/FormsCategoryList/types';

interface Props {
	card: FormReviewCardData;
	isSelected?: boolean;
	onClick: () => void;
}

export const DwFormReviewCard = ({ card, isSelected = false, onClick }: Props) => {
	const tMipManage = useTranslationMipManage();
	const tMipCreate = useTranslationMipCreate();
	const isInherited = card.subjectAction === SubjectAction.NOT_YET_ACTED;
	const status = isInherited ? REQUESTED_STATUS : card.status ?? REQUESTED_STATUS;
	const isSigned = card.signatureStatus === DocumentWorkflowInstanceSignatureStatus.SIGNED;
	const awaitingSignature = isAwaitingRequiredSignature(card);

	const uploadedAt =
		card.status !== null && card.createdAt
			? formatStringDateToUI(card.createdAt, DATE_FORMATS.CALENDAR_DATE)
			: undefined;

	return (
		<CardContainer onClick={onClick} $isSelected={isSelected}>
			<CardBody>
				<Text size="B6Bold" c="dark.8" truncate lineClamp={2}>
					{card.templateName}
				</Text>

				{uploadedAt && (
					<Text size="B6" c="dark.2">
						{`${tMipManage('kyc.review.forms.uploaded_on')} ${uploadedAt}`}
					</Text>
				)}

				<Flex mt="0.25rem">
					<DocumentStatusDot status={status} isInherited={isInherited} />
				</Flex>
			</CardBody>

			<BadgeColumn>
				{card.isSmartForm && (
					<Badge size="xs" variant="outline" color="gray.4" c="dark">
						<TextV2 variant="sans/11/400" center noWrap>
							{tMipCreate('kyc.documents.card.smart_form')}
						</TextV2>
					</Badge>
				)}
				{isSigned && (
					<Badge size="xs" variant="light" color="teal" data-testid="signed-badge">
						<TextV2 variant="sans/11/400" center noWrap>
							{tMipManage('kyc.review.forms.signed')}
						</TextV2>
					</Badge>
				)}
				{awaitingSignature && (
					<Badge
						size="xs"
						variant="light"
						color="orange"
						data-testid="awaiting-signature-badge"
					>
						<TextV2 variant="sans/11/400" center noWrap>
							{tMipManage('kyc.review.forms.awaiting_signature')}
						</TextV2>
					</Badge>
				)}
			</BadgeColumn>
		</CardContainer>
	);
};
